import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames/bind';
import { HeaderV6 } from '../../components/HeaderV6';
import { FooterV2 } from '../../components/FooterV2';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { UpsellProgressBar } from '../../components/UpsellProgressBar';
import { useLegalInfo } from '../../shared/use-legal-info';
import { useUpsellStep } from '../../shared/use-upsell-flow';
import { savePurchaseProduct } from '../../../lib/use-cases/get-purchase';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { amplitudeTracker } from '../../../lib/shared/amplitude-tracker';
import { buyUpsell } from '../../../lib/use-cases/buy-upsell';
import { MK_LETTERS_TO_MYSELF_1799_PRODUCT } from '../../../lib/shared/products';
import { UPSELL_STEPS } from '../../../lib/shared/upsell-steps';
import { LETTERS_V2_STEP } from '../../../lib/shared/upsell-flow-steps';
import footerLogoSrc from '../../assets/images/boundly-logo-pink-light.png';
import coverSrc from '../../assets/images/upsell/letters/cover.png';
import checklistSrc from '../../assets/images/upsell/letters/checklist.png';
import pepTalkSrc from '../../assets/images/upsell/letters/pep-talk.png';
import courageSrc from '../../assets/images/upsell/letters/courage.png';
import goodDaysSrc from '../../assets/images/upsell/letters/good-days.png';
import unsaidSrc from '../../assets/images/upsell/letters/unsaid.png';
import styles from './UpsellLettersPageV2.css';

const cx = classnames.bind(styles);

const PRICE = '$17.99';

const PRODUCT_META = ['36 letters', 'US Letter', '44 pages'];

const DISCLOSURE = `By clicking “Complete purchase” you agree to be charged ${PRICE} for the selected add-on. The PDF is offered on a one-time purchase basis, with no recurring charge. Payment will be processed using the billing details you provided earlier.`;

const WALKTHROUGH = [
    {
        iconSrc: pepTalkSrc,
        title: 'Be your own pep talk',
        text: 'Can’t sleep? Feeling behind? Furious? Open the letter your calmer self wrote for exactly this.'
    },
    {
        iconSrc: courageSrc,
        title: 'Catch your courage',
        text: 'Before a hard conversation or a big “no,” read the proof you left yourself, in your own handwriting.'
    },
    {
        iconSrc: goodDaysSrc,
        title: 'Keep the good days',
        text: 'Pin down the moments you’d forget, like “Open when you’re proud of yourself,” and get them back when life feels flat.'
    },
    {
        iconSrc: unsaidSrc,
        title: 'Say what’s still unsaid',
        text: 'Miss someone? Need to forgive? Say it now, seal it, and open it the day it counts.'
    }
];

const HOW_IT_WORKS = [
    {
        step: 'STEP 1',
        title: 'Write while you’re okay',
        text: 'Pick any letter and answer three prompts. A few honest lines beat a perfect page.'
    },
    {
        step: 'STEP 2',
        title: 'Seal it',
        text: 'Cut out the handmade envelope from the PDF, fold the letter inside and write what it’s for on the front.'
    },
    {
        step: 'STEP 3',
        title: 'Open it on the day',
        text: 'When the day comes, read what the calmer you wrote for you.'
    }
];

function UpsellLettersPageV2() {
    const { subscriptionId, nextUrl } = useUpsellStep(LETTERS_V2_STEP);
    const { legalLinks, legalEntity } = useLegalInfo();
    const [isPending, setIsPending] = useState(false);
    const [isFloatingButtonVisible, setIsFloatingButtonVisible] = useState(false);
    const topActionsRef = useRef(null);
    const bottomActionsRef = useRef(null);

    useEffect(() => {
        logEvent(EVENT_TYPES.UPSELL_LETTERS_PAGE_VISITED);
    }, []);

    useEffect(() => {
        const topActions = topActionsRef.current;
        const bottomActions = bottomActionsRef.current;
        let isTopPassed = false;
        let isBottomReached = false;

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.target === topActions) {
                    isTopPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
                }

                if (entry.target === bottomActions) {
                    isBottomReached = entry.isIntersecting;
                }
            });

            setIsFloatingButtonVisible(isTopPassed && !isBottomReached);
        }, { threshold: 0 });

        [topActions, bottomActions].forEach(element => element && observer.observe(element));

        return () => observer.disconnect();
    }, []);

    const onBuy = async container => {
        setIsPending(true);

        logEvent(EVENT_TYPES.UPSELL_PAGE_CLICK_PAY, { container, 'upsell_type': 'letters' });

        await buyUpsell(subscriptionId, MK_LETTERS_TO_MYSELF_1799_PRODUCT);

        savePurchaseProduct('upsell');
        amplitudeTracker.flush();
        window.location.replace(nextUrl);
    };

    const renderCheckItem = (key, content) => (
        <li key={key} className={styles.checkItem}>
            <Icon name='checkItemFilled' className={styles.checkIcon} />
            <p>{content}</p>
        </li>
    );

    const renderTitle = (Tag = 'h1') => (
        <Tag className={styles.title}>
            Leave your future self <span>36 letters to open</span>
        </Tag>
    );

    const renderProductCard = withBorder => (
        <article className={withBorder ? styles.productCardBordered : styles.productCard}>
            <div className={styles.productImage}>
                <p className={styles.productBadge}>Printable PDF</p>
                <img src={coverSrc} alt='' />
            </div>
            <div className={styles.productInfo}>
                <p className={styles.productName}>Letters to My Future Self</p>
                <ul className={styles.productMeta}>
                    {PRODUCT_META.map(item => (
                        <li key={item}>{item}</li>
                    ))}
                </ul>
            </div>
        </article>
    );

    const renderPrice = () => (
        <div className={styles.price}>
            <p className={styles.priceLabel}>ONE-TIME PAYMENT</p>
            <p className={styles.priceRow}>
                <span className={styles.priceValue}>{PRICE}</span>
                <span className={styles.priceChip}>50¢ PER LETTER</span>
            </p>
        </div>
    );

    const renderActions = (targetClickBlock, ref) => (
        <div className={styles.actions} ref={ref}>
            <Button
                label='Complete purchase'
                onClick={() => onBuy(targetClickBlock)}
                isLoading={isPending}
                type='accent'
                className={styles.buyButton}
            />
            <Button
                label='Skip and continue'
                type='warmOutline'
                className={styles.skipButton}
                onClick={() => {
                    logEvent(EVENT_TYPES.UPSELL_PAGE_SKIPPED, { 'target_click_block': targetClickBlock });
                    amplitudeTracker.flush();
                    window.location.replace(nextUrl);
                }}
            />
        </div>
    );

    return (
        <div className={styles.container}>
            <HeaderV6
                url={window.location.pathname}
                className={styles.header}
                isFinished
                progressBar={<UpsellProgressBar currentStep={UPSELL_STEPS.ADD_UPGRADE} />}
            />
            <main className={styles.main}>
                <section className={styles.offer}>
                    <p className={styles.badge}>
                        <Icon name='starFilled' className={styles.badgeIcon} />
                        ONE-TIME ADD-ON
                    </p>
                    {renderTitle()}
                    <p className={styles.subtitle}>Everything you need to write today and open on the day it counts.</p>
                    <ul className={styles.checkList}>
                        {renderCheckItem('letters', <><strong>36 “Open when…” letters,</strong> each with three prompts and a first sentence</>)}
                        {renderCheckItem('envelope', <><strong>A handmade envelope</strong> to cut, fold and seal</>)}
                        {renderCheckItem('stickers', <><strong>Stickers</strong> to decorate your letters and envelopes</>)}
                        {renderCheckItem('checklist', <><strong>A checklist</strong> for the letters you’ve opened</>)}
                    </ul>
                    {renderProductCard(false)}
                    {renderPrice()}
                    <p className={styles.disclosure}>{DISCLOSURE}</p>
                    {renderActions('body', topActionsRef)}
                    <p className={styles.note}>
                        Your PDF is ready right after you order. Print it at home or at a print shop and write by hand. You can also type into it on a computer.
                    </p>
                </section>
                <section className={styles.walkthrough}>
                    <h2 className={styles.sectionTitle}>A letter waiting for every kind of day</h2>
                    <ul className={styles.walkthroughList}>
                        {WALKTHROUGH.map(({ iconSrc, title, text }) => (
                            <li key={title}>
                                <div className={styles.walkthroughIcon}>
                                    <img src={iconSrc} alt='' />
                                </div>
                                <div>
                                    <p className={styles.walkthroughTitle}>{title}</p>
                                    <p className={styles.walkthroughText}>{text}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
                <section className={styles.selfCheck}>
                    <div className={styles.selfCheckPicture}>
                        <img src={checklistSrc} alt='' />
                    </div>
                    <h2 className={styles.sectionTitle}>Does this sound like you?</h2>
                    <ul className={styles.checkList}>
                        {renderCheckItem('advice', <>You give great advice to everyone else, and <strong>forget to take it yourself</strong></>)}
                        {renderCheckItem('fade', <>The <strong>good days fade faster</strong> than the bad ones</>)}
                        {renderCheckItem('unsaid', <>There are things you’ve <strong>never said out loud</strong>, to anyone</>)}
                        {renderCheckItem('real', <>You’d like to <strong>leave yourself something real</strong> for a day that matters</>)}
                    </ul>
                </section>
                <section className={styles.howItWorks}>
                    <h2 className={styles.sectionTitle}>How it works</h2>
                    {HOW_IT_WORKS.map(({ step, title, text }) => (
                        <article key={step} className={styles.howItWorksStep}>
                            <p className={styles.stepLabel}>{step}</p>
                            <p className={styles.walkthroughTitle}>{title}</p>
                            <p className={styles.walkthroughText}>{text}</p>
                        </article>
                    ))}
                </section>
                <section className={styles.bottomOffer}>
                    {renderTitle('h2')}
                    {renderProductCard(true)}
                    {renderPrice()}
                    <p className={styles.disclosureBordered}>{DISCLOSURE}</p>
                    {renderActions('body_bottom', bottomActionsRef)}
                </section>
            </main>
            <div className={cx('floatingButton', { floatingButtonVisible: isFloatingButtonVisible })}>
                <Button
                    label='Complete purchase'
                    onClick={() => onBuy('floating')}
                    isLoading={isPending}
                    type='accent'
                    className={styles.buyButton}
                />
            </div>
            <FooterV2
                legalEntity={legalEntity}
                legalLinks={legalLinks}
                logoSrc={footerLogoSrc}
            />
        </div>
    );
}

export {
    UpsellLettersPageV2
};
